import React from 'react';
import { X } from 'lucide-react';

const DeploymentDetailsModal = ({
    selectedDeployment,
    onClose
}) => {
    if (!selectedDeployment) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-5 border-b border-gray-800 flex items-center justify-between bg-gradient-to-r from-gray-900 to-gray-800/50">
                    <div className="flex items-center gap-3">
                        <h2 className="text-2xl font-bold">{selectedDeployment.tag}</h2>
                        {selectedDeployment.status && (
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                                selectedDeployment.status === 'Success' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                                selectedDeployment.status === 'Failed' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                'bg-blue-500/20 text-blue-400 border-blue-500/30'
                            }`}>
                                {selectedDeployment.status}
                            </span>
                        )}
                        {selectedDeployment.deployment_type && (
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                                selectedDeployment.deployment_type === 'live' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                            }`}>
                                {selectedDeployment.deployment_type}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {selectedDeployment.details && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-lg p-3.5 hover:border-blue-500/40 transition-colors">
                                <p className="text-xs font-medium text-blue-400 mb-1.5 uppercase tracking-wide">Build Number</p>
                                <p className="text-white font-mono text-sm">{selectedDeployment.details.build_number}</p>
                            </div>
                            <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-lg p-3.5 hover:border-purple-500/40 transition-colors">
                                <p className="text-xs font-medium text-purple-400 mb-1.5 uppercase tracking-wide">Commit ID</p>
                                <p className="text-white font-mono text-sm truncate" title={selectedDeployment.details.commit_id}>{selectedDeployment.details.commit_id}</p>
                            </div>
                            <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-lg p-3.5 hover:border-orange-500/40 transition-colors">
                                <p className="text-xs font-medium text-orange-400 mb-1.5 uppercase tracking-wide">Branch Name</p>
                                <p className="text-white font-mono text-sm">{selectedDeployment.branch || selectedDeployment.details.branch_name || 'N/A'}</p>
                            </div>
                            {selectedDeployment.approved_by && (
                                <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/20 rounded-lg p-3.5 hover:border-cyan-500/40 transition-colors">
                                    <p className="text-xs font-medium text-cyan-400 mb-1.5 uppercase tracking-wide">Approved By</p>
                                    <p className="text-white text-sm">{selectedDeployment.approved_by}</p>
                                </div>
                            )}
                            <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-lg p-3.5 hover:border-green-500/40 transition-colors md:col-span-2">
                                <p className="text-xs font-medium text-green-400 mb-1.5 uppercase tracking-wide">Commit Message</p>
                                <p className="text-white text-sm">{selectedDeployment.details.commit_message}</p>
                            </div>
                            <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-500/20 rounded-lg p-3.5 hover:border-yellow-500/40 transition-colors">
                                <p className="text-xs font-medium text-yellow-400 mb-1.5 uppercase tracking-wide">Author</p>
                                <p className="text-white text-sm">{selectedDeployment.details.author_name}</p>
                                <p className="text-gray-400 text-xs mt-0.5">{selectedDeployment.details.author_email}</p>
                            </div>
                            <div className="bg-gradient-to-br from-pink-500/10 to-pink-600/5 border border-pink-500/20 rounded-lg p-3.5 hover:border-pink-500/40 transition-colors">
                                <p className="text-xs font-medium text-pink-400 mb-1.5 uppercase tracking-wide">Deployed At</p>
                                <p className="text-white text-sm">{selectedDeployment.details?.date_time ? (() => {
                                    // Parse the date string as-is without timezone conversion
                                    const dateStr = selectedDeployment.details.date_time;
                                    const date = new Date(dateStr.replace(' ', 'T')); // Convert to ISO format
                                    return date.toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        hour12: true
                                    });
                                })() : 'No date available'}</p>
                            </div>
                            <div className="bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border border-indigo-500/20 rounded-lg p-3.5 hover:border-indigo-500/40 transition-colors">
                                <p className="text-xs font-medium text-indigo-400 mb-1.5 uppercase tracking-wide">Domain</p>
                                <p className="text-white font-mono text-sm break-all">{selectedDeployment.details.domain_name}</p>
                            </div>
                            <div className="bg-gradient-to-br from-teal-500/10 to-teal-600/5 border border-teal-500/20 rounded-lg p-3.5 hover:border-teal-500/40 transition-colors">
                                <p className="text-xs font-medium text-teal-400 mb-1.5 uppercase tracking-wide">Image Name</p>
                                <p className="text-white font-mono text-sm break-all">{selectedDeployment.details.image_name}</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-800 bg-gray-900/50 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeploymentDetailsModal;
